import React, { memo } from 'react';
import { FaPlus, FaTrash } from 'react-icons/fa';
import { CreateOptionTypeRequest, OptionType } from '../../types/optionTypes';

interface OptionFormProps {
  formData: CreateOptionTypeRequest;
  optionInput: string;
  editingOption: OptionType | null;
  isFormValid: boolean;
  onFormDataChange: (updates: Partial<CreateOptionTypeRequest>) => void;
  onOptionInputChange: (value: string) => void;
  onAddOption: () => void;
  onRemoveOption: (option: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  onCancel: () => void;
}

export const OptionForm: React.FC<OptionFormProps> = memo(props => {
  const {
    formData,
    optionInput,
    editingOption,
    isFormValid,
    onFormDataChange,
    onOptionInputChange,
    onAddOption,
    onRemoveOption,
    onSubmit,
    onCancel,
  } = props;

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      onAddOption();
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
      <div className="p-6 border-b border-gray-200">
        <h3 className="text-xl font-bold text-gray-900">
          {editingOption ? 'Seçenek Türünü Düzenle' : 'Yeni Seçenek Türü Ekle'}
        </h3>
      </div>

      <form onSubmit={onSubmit} className="p-6 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Sistem Adı *</label>
            <input
              type="text"
              value={formData.name}
              onChange={e => onFormDataChange({ name: e.target.value })}
              placeholder="boyut, seker, dem..."
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Görünen Ad *</label>
            <input
              type="text"
              value={formData.display_name}
              onChange={e => onFormDataChange({ display_name: e.target.value })}
              placeholder="Boyut, Şeker, Dem Kuvveti..."
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Seçenekler *</label>
          <div className="flex gap-2 mb-3">
            <input
              type="text"
              value={optionInput}
              onChange={e => onOptionInputChange(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Seçenek ekle..."
              className="flex-1 px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <button
              type="button"
              onClick={onAddOption}
              className="px-4 py-3 bg-blue-500 text-white rounded-xl hover:bg-blue-600 transition-all"
            >
              <FaPlus />
            </button>
          </div>

          <div className="flex flex-wrap gap-2">
            {formData.options.map((option, index) => (
              <div key={index} className="flex items-center gap-2 bg-gray-100 px-3 py-2 rounded-lg">
                <span>{option}</span>
                <button
                  type="button"
                  onClick={() => onRemoveOption(option)}
                  className="text-red-500 hover:text-red-700"
                >
                  <FaTrash className="text-xs" />
                </button>
              </div>
            ))}
          </div>
        </div>

        {formData.options.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Varsayılan Değer *
            </label>
            <select
              value={formData.default_value}
              onChange={e => onFormDataChange({ default_value: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            >
              <option value="">Varsayılan değer seçin...</option>
              {formData.options.map((option, index) => (
                <option key={index} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Sıralama</label>
          <input
            type="number"
            value={formData.sort_order}
            onChange={e => onFormDataChange({ sort_order: parseInt(e.target.value) || 0 })}
            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            min="0"
          />
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={onCancel}
            className="px-6 py-3 text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 transition-all"
          >
            İptal
          </button>
          <button
            type="submit"
            disabled={!isFormValid}
            className="px-6 py-3 bg-blue-500 text-white rounded-xl hover:bg-blue-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {editingOption ? 'Güncelle' : 'Ekle'}
          </button>
        </div>
      </form>
    </div>
  );
});

OptionForm.displayName = 'OptionForm';
